"use client";

import { useRef, type MutableRefObject, type RefObject } from "react";
import type {
  ScrollHintState,
  TimelineViewportState,
} from "./timeline-root-types";
import { useTimelineCoordinateReaders } from "./useTimelineCoordinateReaders";
import { useTimelinePeriodRefs } from "./useTimelinePeriodRefs";
import { useTimelinePeriodScroller } from "./useTimelinePeriodScroller";
import { useTimelineScrollUiState } from "./useTimelineScrollUiState";

export interface UseTimelineScrollControllerInput {
  layoutContentWidth: number | undefined;
  periodCount: number;
}

export interface TimelineScrollController {
  centeredPeriodIndex: number;
  getInnerXFromClient: (clientX: number) => number | null;
  getViewportCenterInInner: () => number | null;
  handleScrollNext: () => void;
  handleScrollPrev: () => void;
  innerRef: RefObject<HTMLDivElement | null>;
  periodRefs: MutableRefObject<Array<HTMLDivElement | null>>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollHintState: ScrollHintState;
  viewportState: TimelineViewportState;
}

export function useTimelineScrollController({
  layoutContentWidth,
  periodCount,
}: UseTimelineScrollControllerInput): TimelineScrollController {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const periodRefs = useTimelinePeriodRefs({ periodCount });
  const coordinateReaders = useTimelineCoordinateReaders({
    innerRef,
    scrollContainerRef,
  });
  const scrollUiState = useTimelineScrollUiState({
    innerRef,
    layoutContentWidth,
    scrollContainerRef,
  });
  const periodScroller = useTimelinePeriodScroller({
    centeredPeriodIndex: scrollUiState.centeredPeriodIndex,
    periodCount,
    periodRefs,
    scrollContainerRef,
  });

  return {
    centeredPeriodIndex: scrollUiState.centeredPeriodIndex,
    getInnerXFromClient: coordinateReaders.getInnerXFromClient,
    getViewportCenterInInner: coordinateReaders.getViewportCenterInInner,
    handleScrollNext: periodScroller.handleScrollNext,
    handleScrollPrev: periodScroller.handleScrollPrev,
    innerRef,
    periodRefs,
    scrollContainerRef,
    scrollHintState: scrollUiState.scrollHintState,
    viewportState: scrollUiState.viewportState,
  };
}
